import React, { createContext, useContext, useEffect, useState } from 'react';

import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { Project } from '@qality/common-model';
import {
  ProjectService,
  StorageOfUniqueServices,
} from '@qality/common-service';

type QAlityPageConfigurationContextValue = {
  isQAlityConfigured: boolean;
  project: Project;
};

const initialContextValue: QAlityPageConfigurationContextValue = {
  isQAlityConfigured: false,
  project: null,
};

export const QAlityPageConfigurationContext =
  createContext<QAlityPageConfigurationContextValue>(initialContextValue);

export const useIsQAlityConfiguredContextValue = (): boolean => {
  return useContext(QAlityPageConfigurationContext).isQAlityConfigured;
};

export const useProjectContextValue = (): Project => {
  return useContext(QAlityPageConfigurationContext).project;
};

type Props = { children: React.ReactNode };

export function QAlityPageConfigurationProvider({
  children,
}: Props): JSX.Element {
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true);
  const [isQAlityConfigured, setIsQAlityConfigured] = useState(false);
  const [project, setProject] = useState(null);

  const getProjectKeyFromUrl = async () => {
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getJiraURLSearchParams();

    return urlSearchParams.get('project.key');
  };

  const getIsQalityConfigured = async (projectKey: string) => {
    if (projectKey) {
      return ProjectService.isQAlityConfiguredInGivenProject(projectKey);
    }

    return ProjectService.isQAlityConfiguredInAnyProject();
  };

  const getProject = async (projectKey: string) => {
    if (projectKey) {
      const projectDto = await ProjectService.getProject(projectKey);
      return new Project(projectDto);
    }

    return null;
  };

  useEffect(() => {
    const init = async () => {
      const projectKey = await getProjectKeyFromUrl();

      const [newIsQalityConfigured, newProject] = await Promise.all([
        getIsQalityConfigured(projectKey),
        getProject(projectKey),
      ]);

      setIsQAlityConfigured(newIsQalityConfigured);

      setProject(newProject);

      setIsLoadingConfiguration(false);
    };

    init();
  }, []);

  return (
    <QAlityPageConfigurationContext.Provider
      value={{
        isQAlityConfigured,
        project,
      }}
    >
      {isLoadingConfiguration ? <SpinnerLoader /> : children}
    </QAlityPageConfigurationContext.Provider>
  );
}
